import { atom, type ReadableAtom, type WritableAtom } from 'nanostores';
import type { EditorDocument, ScrollPosition } from '~/types/editor';
import { runtimePromise } from '~/lib/runtime';
import type { ITerminal } from '~/types/terminal';
import { EditorStore } from './editor';
import { FilesStore, type FileMap } from './files';
import { PreviewsStore } from './previews';
import { TerminalStore } from './terminal';
import { clearStreamingFile } from './streaming-file';
import {
  buildFixedTabs,
  insertToolTab,
  TOOL_LABELS,
  TOOLS_BY_PROJECT_TYPE,
  type PanelTab,
  type ProjectType,
  type ToolType,
} from './panel-tabs';
import type { ActionAlert } from '~/types/actions';
import { WORK_DIR } from '~/utils/constants';
import { BREAKPOINTS } from '~/lib/hooks/useMediaQuery';

/**
 * Below `lg` the workbench is a full-screen overlay (Workbench.client), so
 * opening it on its own covers the chat mid-build. There, only explicit user
 * actions (Preview / Code / Publish) open it.
 */
function isCompactViewport() {
  return typeof window !== 'undefined' && window.matchMedia(`(max-width: ${BREAKPOINTS.lg - 0.02}px)`).matches;
}

// ─── Panel Tab Types ───

export { TOOLS_BY_PROJECT_TYPE, TOOL_LABELS, LAUNCHER_TAB_ID } from './panel-tabs';
export type { PanelTab, ProjectType, ToolType } from './panel-tabs';

export class WorkbenchStore {
  #previewsStore = new PreviewsStore(runtimePromise);
  #filesStore = new FilesStore(runtimePromise);
  #editorStore = new EditorStore(this.#filesStore);
  #terminalStore = new TerminalStore(runtimePromise);

  showWorkbench: WritableAtom<boolean> = import.meta.hot?.data.showWorkbench ?? atom(false);
  // Set when the user explicitly closes the preview panel (→ full-width chat), so
  // the auto-open effects don't yank it back. Cleared whenever the panel is
  // reopened (openTool / openPreview) or a new chat starts.
  userClosedWorkbench: WritableAtom<boolean> = import.meta.hot?.data.userClosedWorkbench ?? atom(false);
  unsavedFiles: WritableAtom<Set<string>> = import.meta.hot?.data.unsavedFiles ?? atom(new Set<string>());
  actionAlert: WritableAtom<ActionAlert | undefined> =
    import.meta.hot?.data.actionAlert ?? atom<ActionAlert | undefined>(undefined);
  panelTabs: WritableAtom<PanelTab[]> = atom<PanelTab[]>(buildFixedTabs('landing'));
  activeTabId: WritableAtom<string> = atom<string>('preview');
  projectType: WritableAtom<ProjectType> = atom<ProjectType>('landing');
  // Bumped each time a generation finishes. The Preview panel is unmounted while
  // the code tab is shown during a build, so it can miss the "streaming ended"
  // transition and keep showing the pre-build page. Preview reads this nonce as
  // a cache-bust on (re)mount so it always loads the freshly generated site.
  previewReloadNonce: WritableAtom<number> = atom<number>(0);
  modifiedFiles = new Set<string>();
  #globalExecutionQueue = Promise.resolve();
  constructor() {
    if (import.meta.hot) {
      import.meta.hot.data.unsavedFiles = this.unsavedFiles;
      import.meta.hot.data.showWorkbench = this.showWorkbench;
      import.meta.hot.data.userClosedWorkbench = this.userClosedWorkbench;
      import.meta.hot.data.actionAlert = this.actionAlert;

      // Ensure binary files are properly preserved across hot reloads
      const filesMap = this.files.get();

      for (const [path, dirent] of Object.entries(filesMap)) {
        if (dirent?.type === 'file' && dirent.isBinary && dirent.content) {
          // Make sure binary content is preserved
          this.files.setKey(path, { ...dirent });
        }
      }
    }
  }

  addToExecutionQueue(callback: () => Promise<void>) {
    this.#globalExecutionQueue = this.#globalExecutionQueue.then(() => callback());
  }

  get previews() {
    return this.#previewsStore.previews;
  }

  get files() {
    return this.#filesStore.files;
  }

  get currentDocument(): ReadableAtom<EditorDocument | undefined> {
    return this.#editorStore.currentDocument;
  }

  get selectedFile(): ReadableAtom<string | undefined> {
    return this.#editorStore.selectedFile;
  }

  get filesCount(): number {
    return this.#filesStore.filesCount;
  }

  get showTerminal() {
    return this.#terminalStore.showTerminal;
  }
  get duneaTerminal() {
    return this.#terminalStore.duneaTerminal;
  }
  get alert() {
    return this.actionAlert;
  }
  clearAlert() {
    this.actionAlert.set(undefined);
  }

  toggleTerminal(value?: boolean) {
    this.#terminalStore.toggleTerminal(value);
  }

  attachTerminal(terminal: ITerminal) {
    this.#terminalStore.attachTerminal(terminal);
  }
  attachDuneaTerminal(terminal: ITerminal) {
    this.#terminalStore.attachDuneaTerminal(terminal);
  }

  detachTerminal(terminal: ITerminal) {
    this.#terminalStore.detachTerminal(terminal);
  }

  onTerminalResize(cols: number, rows: number) {
    this.#terminalStore.onTerminalResize(cols, rows);
  }

  setDocuments(files: FileMap) {
    this.#editorStore.setDocuments(files);

    if (this.#filesStore.filesCount > 0 && this.currentDocument.get() === undefined) {
      // we find the first file and select it
      for (const [filePath, dirent] of Object.entries(files)) {
        if (dirent?.type === 'file') {
          this.setSelectedFile(filePath);
          break;
        }
      }
    }
  }

  setShowWorkbench(show: boolean) {
    this.showWorkbench.set(show);
  }

  /**
   * Open the workbench for a system event (chat started, preview ready, files
   * restored). No-op on compact viewports, and — unless `force` — after the
   * user closed it.
   */
  autoShowWorkbench({ force = false }: { force?: boolean } = {}) {
    if (isCompactViewport() || (!force && this.userClosedWorkbench.get())) {
      return;
    }

    this.showWorkbench.set(true);
  }

  /** User closed the preview → full-width chat. Sticks until the panel is reopened. */
  hideWorkbench() {
    this.userClosedWorkbench.set(true);
    this.showWorkbench.set(false);
  }

  /** Reopen the preview (from the post-generation Preview/Publish buttons). */
  reopenWorkbench(tool?: ToolType) {
    this.userClosedWorkbench.set(false);

    if (tool) {
      this.openTool(tool);
    } else {
      this.setActiveTab('preview');
      this.showWorkbench.set(true);
    }
  }

  setProjectType(type: ProjectType) {
    this.projectType.set(type);
  }

  getAvailableTools(): ToolType[] {
    return TOOLS_BY_PROJECT_TYPE[this.projectType.get()];
  }

  /**
   * Refresh a file from the sandbox runtime after a tool-based write.
   * Reads the file content from the sandbox and updates the files store + editor.
   */
  async refreshFileFromSandbox(filePath: string) {
    try {
      const sandboxRuntime = await runtimePromise;

      // Normalize the path relative to workdir
      const relativePath = filePath.startsWith('/home/project/') ? filePath.replace('/home/project/', '') : filePath;

      const content = await sandboxRuntime.fs.readFile(relativePath, 'utf-8');
      const fullPath = `${sandboxRuntime.workdir}/${relativePath}`;

      // Update the files store
      this.#filesStore.files.setKey(fullPath, {
        type: 'file',
        content,
        isBinary: false,
      });

      // Update editor if this file is currently open
      this.#editorStore.updateFile(fullPath, content);

      // Show workbench and select the file
      this.autoShowWorkbench();

      this.setSelectedFile(fullPath);
    } catch (error) {
      // File may not exist yet if the tool write failed — ignore silently
      console.warn(`Failed to refresh file from sandbox: ${filePath}`, error);
    }
  }

  openPreview() {
    this.userClosedWorkbench.set(false);
    this.showWorkbench.set(true);
  }

  // ─── Panel Tab Management ───

  closeTab(tabId: string): void {
    const tabs = this.panelTabs.get();
    const idx = tabs.findIndex((t) => t.id === tabId);
    const tab = tabs[idx];

    if (!tab || !tab.closable) {
      return;
    }

    const newTabs = tabs.filter((t) => t.id !== tabId);
    this.panelTabs.set(newTabs);

    // If closing the active tab, fall back to the Preview tab — a predictable
    // home, rather than the previous tab (which may be the Publish panel).
    if (this.activeTabId.get() === tabId) {
      this.activeTabId.set('preview');
    }
  }

  setActiveTab(tabId: string): void {
    this.activeTabId.set(tabId);
  }

  openTool(tool: ToolType): void {
    // Opening a tool always (re)opens the panel and clears a prior manual close.
    this.userClosedWorkbench.set(false);
    this.showWorkbench.set(true);

    const tabs = this.panelTabs.get();

    // Check if the tool is already open
    const existingTab = tabs.find((t) => t.tool === tool);

    if (existingTab) {
      this.activeTabId.set(existingTab.id);
      return;
    }

    // Create a new closable tab, inserted before the launcher so it stays last
    const newTab: PanelTab = {
      id: `tool-${Date.now()}`,
      type: 'tool',
      tool,
      label: TOOL_LABELS[tool],
      closable: true,
    };

    this.panelTabs.set(insertToolTab(tabs, newTab));
    this.activeTabId.set(newTab.id);
  }

  /**
   * Reset state for a new chat — clears previews, files, and editor.
   */
  resetForNewChat() {
    this.#previewsStore.clearPreviews();
    this.showWorkbench.set(false);
    this.userClosedWorkbench.set(false);
    this.panelTabs.set(buildFixedTabs(this.projectType.get()));
    this.activeTabId.set('preview');

    // Drop any in-flight/finished streamed file so it can't leak into the new chat.
    clearStreamingFile();
  }

  setCurrentDocumentContent(newContent: string) {
    const filePath = this.currentDocument.get()?.filePath;

    if (!filePath) {
      return;
    }

    const originalContent = this.#filesStore.getFile(filePath)?.content;
    const unsavedChanges = originalContent !== undefined && originalContent !== newContent;

    this.#editorStore.updateFile(filePath, newContent);

    const currentDocument = this.currentDocument.get();

    if (currentDocument) {
      const previousUnsavedFiles = this.unsavedFiles.get();

      if (unsavedChanges && previousUnsavedFiles.has(currentDocument.filePath)) {
        return;
      }

      const newUnsavedFiles = new Set(previousUnsavedFiles);

      if (unsavedChanges) {
        newUnsavedFiles.add(currentDocument.filePath);
      } else {
        newUnsavedFiles.delete(currentDocument.filePath);
      }

      this.unsavedFiles.set(newUnsavedFiles);
    }
  }

  setCurrentDocumentScrollPosition(position: ScrollPosition) {
    const editorDocument = this.currentDocument.get();

    if (!editorDocument) {
      return;
    }

    const { filePath } = editorDocument;

    this.#editorStore.updateScrollPosition(filePath, position);
  }

  setSelectedFile(filePath: string | undefined) {
    this.#editorStore.setSelectedFile(filePath);

    // Files seeded from the workspace listing carry a path but no bytes; read
    // them the first time they are opened. Fire-and-forget — the editor renders
    // the moment the content lands, and a failure leaves the tree intact.
    if (filePath) {
      void this.#loadPendingContent(filePath);
    }
  }

  /** Fetch a `contentPending` file's bytes and drop them into the store + editor. */
  async #loadPendingContent(filePath: string) {
    const dirent = this.#filesStore.getFile(filePath);

    if (!dirent?.contentPending) {
      return;
    }

    try {
      const sandboxRuntime = await runtimePromise;
      const relativePath = filePath.startsWith(`${WORK_DIR}/`) ? filePath.slice(WORK_DIR.length + 1) : filePath;
      const content = await sandboxRuntime.fs.readFile(relativePath, 'utf-8');

      // Re-check: a watch event may have delivered the real content while this
      // read was in flight, and that copy is the fresher one.
      if (!this.#filesStore.getFile(filePath)?.contentPending) {
        return;
      }

      this.#filesStore.files.setKey(filePath, { type: 'file', content, isBinary: false });
      this.#editorStore.updateFile(filePath, content);
    } catch {
      // Unreadable (binary, deleted, pod asleep) — leave the placeholder so the
      // path still shows in the tree rather than vanishing from it.
    }
  }

  async saveFile(filePath: string) {
    const documents = this.#editorStore.documents.get();
    const document = documents[filePath];

    if (document === undefined) {
      return;
    }

    /*
     * For scoped locks, we would need to implement diff checking here
     * to determine if the user is modifying existing code or just adding new code
     * This is a more complex feature that would be implemented in a future update
     */

    await this.#filesStore.saveFile(filePath, document.value);

    const newUnsavedFiles = new Set(this.unsavedFiles.get());
    newUnsavedFiles.delete(filePath);

    this.unsavedFiles.set(newUnsavedFiles);
  }

  async saveCurrentDocument() {
    const currentDocument = this.currentDocument.get();

    if (currentDocument === undefined) {
      return;
    }

    await this.saveFile(currentDocument.filePath);
  }

  resetCurrentDocument() {
    const currentDocument = this.currentDocument.get();

    if (currentDocument === undefined) {
      return;
    }

    const { filePath } = currentDocument;
    const file = this.#filesStore.getFile(filePath);

    if (!file) {
      return;
    }

    this.setCurrentDocumentContent(file.content);
  }

  async saveAllFiles() {
    for (const filePath of this.unsavedFiles.get()) {
      await this.saveFile(filePath);
    }
  }

  getFileModifcations() {
    return this.#filesStore.getFileModifications();
  }

  getModifiedFiles() {
    return this.#filesStore.getModifiedFiles();
  }

  resetAllFileModifications() {
    this.#filesStore.resetFileModifications();
  }

  /**
   * Lock a file to prevent edits
   * @param filePath Path to the file to lock
   * @returns True if the file was successfully locked
   */
  lockFile(filePath: string) {
    return this.#filesStore.lockFile(filePath);
  }

  /**
   * Lock a folder and all its contents to prevent edits
   * @param folderPath Path to the folder to lock
   * @returns True if the folder was successfully locked
   */
  lockFolder(folderPath: string) {
    return this.#filesStore.lockFolder(folderPath);
  }

  /**
   * Unlock a file to allow edits
   * @param filePath Path to the file to unlock
   * @returns True if the file was successfully unlocked
   */
  unlockFile(filePath: string) {
    return this.#filesStore.unlockFile(filePath);
  }

  /**
   * Unlock a folder and all its contents to allow edits
   * @param folderPath Path to the folder to unlock
   * @returns True if the folder was successfully unlocked
   */
  unlockFolder(folderPath: string) {
    return this.#filesStore.unlockFolder(folderPath);
  }

  /**
   * Check if a file is locked
   * @param filePath Path to the file to check
   * @returns Object with locked status, lock mode, and what caused the lock
   */
  isFileLocked(filePath: string) {
    return this.#filesStore.isFileLocked(filePath);
  }

  /**
   * Check if a folder is locked
   * @param folderPath Path to the folder to check
   * @returns Object with locked status, lock mode, and what caused the lock
   */
  isFolderLocked(folderPath: string) {
    return this.#filesStore.isFolderLocked(folderPath);
  }

  async createFile(filePath: string, content: string | Uint8Array = '') {
    try {
      const success = await this.#filesStore.createFile(filePath, content);

      if (success) {
        this.setSelectedFile(filePath);

        /*
         * For empty files, we need to ensure they're not marked as unsaved
         * Only check for empty string, not empty Uint8Array
         */
        if (typeof content === 'string' && content === '') {
          const newUnsavedFiles = new Set(this.unsavedFiles.get());
          newUnsavedFiles.delete(filePath);
          this.unsavedFiles.set(newUnsavedFiles);
        }
      }

      return success;
    } catch (error) {
      console.error('Failed to create file:', error);
      throw error;
    }
  }

  async createFolder(folderPath: string) {
    try {
      return await this.#filesStore.createFolder(folderPath);
    } catch (error) {
      console.error('Failed to create folder:', error);
      throw error;
    }
  }

  async deleteFile(filePath: string) {
    try {
      const currentDocument = this.currentDocument.get();
      const isCurrentFile = currentDocument?.filePath === filePath;

      const success = await this.#filesStore.deleteFile(filePath);

      if (success) {
        const newUnsavedFiles = new Set(this.unsavedFiles.get());

        if (newUnsavedFiles.has(filePath)) {
          newUnsavedFiles.delete(filePath);
          this.unsavedFiles.set(newUnsavedFiles);
        }

        if (isCurrentFile) {
          const files = this.files.get();
          let nextFile: string | undefined = undefined;

          for (const [path, dirent] of Object.entries(files)) {
            if (dirent?.type === 'file') {
              nextFile = path;
              break;
            }
          }

          this.setSelectedFile(nextFile);
        }
      }

      return success;
    } catch (error) {
      console.error('Failed to delete file:', error);
      throw error;
    }
  }

  async deleteFolder(folderPath: string) {
    try {
      const currentDocument = this.currentDocument.get();
      const isInCurrentFolder = currentDocument?.filePath?.startsWith(folderPath + '/');

      const success = await this.#filesStore.deleteFolder(folderPath);

      if (success) {
        const unsavedFiles = this.unsavedFiles.get();
        const newUnsavedFiles = new Set<string>();

        for (const file of unsavedFiles) {
          if (!file.startsWith(folderPath + '/')) {
            newUnsavedFiles.add(file);
          }
        }

        if (newUnsavedFiles.size !== unsavedFiles.size) {
          this.unsavedFiles.set(newUnsavedFiles);
        }

        if (isInCurrentFolder) {
          const files = this.files.get();
          let nextFile: string | undefined = undefined;

          for (const [path, dirent] of Object.entries(files)) {
            if (dirent?.type === 'file') {
              nextFile = path;
              break;
            }
          }

          this.setSelectedFile(nextFile);
        }
      }

      return success;
    } catch (error) {
      console.error('Failed to delete folder:', error);
      throw error;
    }
  }

  abortAllActions() {
    // TODO: what do we wanna do and how do we wanna recover from this?
  }
}

export const workbenchStore = new WorkbenchStore();
